// Story E6.3.2 — client REST de presence (Epic E6.3).
// Backend canonical: V1/backend/app/api/v1/endpoints/presence.py
//
// Endpoints consumidos:
//   GET /presence/users?include_company=true  → hidrata sidebar inicial
//   GET /presence/users?user_ids=<uuid>&user_ids=<uuid>  → bulk on-demand
//   GET /presence/me                          → atalho self
//   PUT /presence/me/status                   → grava o status manual (Ausente/Ocupado)
//
// Pegadinha herdada da memória E10.2.4 + E6.1.3: FastAPI Query(list) só
// aceita `key=v1&key=v2`, NÃO `key[]=...`. Axios default emite `[]` ⇒
// precisa `paramsSerializer` inline (já documentado no projeto).

import apiClient from "@/lib/apiClient";

export type PresenceStatus = "online" | "away" | "busy" | "offline";

export interface PresenceEntry {
  user_id: string;
  status: PresenceStatus;
  last_seen: string; // ISO 8601
  company_id: string;
}

export interface PresenceBulkResponse {
  users: Record<string, PresenceEntry>;
}

/** Status que o cliente pode enviar via heartbeat WS (sem `offline`). */
export type SettableStatus = Exclude<PresenceStatus, "offline">;

/**
 * Hidrata estado inicial company-wide (até 500 entries — hard cap backend).
 * Users ausentes do dict = offline (sem key Redis = sem TTL ativo).
 */
export async function fetchCompanyPresence(): Promise<PresenceBulkResponse> {
  const { data } = await apiClient.get<PresenceBulkResponse>("/presence/users", {
    params: { include_company: true },
  });
  return data;
}

/**
 * Bulk on-demand (ex: ao abrir uma DM, garantir status do interlocutor
 * mesmo que ele não esteja no hydrate inicial por algum motivo).
 */
export async function fetchPresenceForUsers(userIds: string[]): Promise<PresenceBulkResponse> {
  if (userIds.length === 0) return { users: {} };
  const { data } = await apiClient.get<PresenceBulkResponse>("/presence/users", {
    params: { user_ids: userIds },
    // FastAPI Query(list[UUID]) só lê repeat `?user_ids=a&user_ids=b`.
    paramsSerializer: {
      serialize: (params: Record<string, unknown>) => {
        const sp = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
          if (Array.isArray(value)) {
            for (const v of value) sp.append(key, String(v));
          } else if (value !== undefined && value !== null) {
            sp.append(key, String(value));
          }
        }
        return sp.toString();
      },
    },
  });
  return data;
}

/** Self presence (null se WS ainda não conectou ou TTL expirou). */
export async function fetchMyPresence(): Promise<PresenceEntry | null> {
  const { data } = await apiClient.get<PresenceEntry | null>("/presence/me");
  return data;
}

/**
 * Grava o status que a pessoa escolheu no menu. Vale ATÉ ELA MUDAR: o servidor guarda
 * (`users.status_manual`) e aplica por cima do heartbeat de todas as abas e aparelhos,
 * sobrevive a recarregar e a fechar o navegador. `online` = voltar ao automático.
 *
 * O caminho NUNCA leva `/api/v1`: o `VITE_API_URL` já traz.
 */
export async function definirStatusManual(status: SettableStatus): Promise<void> {
  await apiClient.put("/presence/me/status", { status });
}
